import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "../../components/Navbar/Navbar";
import { Drawer } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import ShimmerLoader from "../../loading";
import "../../styles.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { debounce } from "@mui/material";

// ✅ removed GetAllCustomers from this import
import { createNewCustomer, getCustomers, updateExistingCustomer } from "../../API  Functions/SessionHistoryFunctions";

import ExportCasInOutReportModal from "./ExportCasInOutReportModal";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import { toast } from "react-toastify";
import i18next from "i18next";
import SelectBox from "../../components/CustomWidgets/SelectBox";
import SearchBox from "../../components/CustomWidgets/SearchBox";
import { getCreditPaymentHistory } from "../../API  Functions/CashierFunctions";
import moment from "moment";
import { checkPermission } from "../../Utils/permissionHelper";
import AddCustomerModal from "../../modals/AddCustomerModal";
import ImportCustomers from "./ImportCustomers";
import NewCustomersTable from "./NewCustomersTable";

const NewAllCustomers = () => {
    const { t } = useTranslation();
    const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
    const [loading, setloading] = useState(false);
    const [currentLimit, setCurrentLimit] = useState(10);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [query, setquery] = useState("");
    const [creditHistoryData, setCreditHistoryData] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [isOpenImportCustomer, setIsOpenImportCustomer] = useState(false);
    const [selectedPaymentType, setSelectedPaymentType] = useState("");
    const [paymentHistoryData, setPaymentHistoryData] = useState([]);
    const [drawerData, setDrawerData] = useState(null);
    const [visible, setVisible] = useState(false);
    const [openAddCustomer, setOpenAddCustomer] = useState(false);
    const printRef = useRef();

    const company_id = useSelector((state) => state?.login?.user?.result?.company_id);
    const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);
    const allStores = useSelector((state) => state?.storeReducer?.selectedStore?.is_select_all_store);

    const options = [
        { value: "", label: t("All") },
        { value: "paid", label: t("Paid") },
        { value: "unpaid", label: t("Unpaid") },
    ];

    const getCurrentDate = () => new Date().toISOString().split("T")[0];

    // ✅ Single fetch function — no duplicate
    const fetchCustomers = useCallback(async (text, type, offset, limit) => {
        const payload = {
            offset,
            limit,
            store_id: company_id,
            search: text,
            type: type || "",
        };
        try {
            const res = await getCustomers(payload);
            if (res?.result?.status_code === 200) {
                setCreditHistoryData(res?.result?.customer_list);
            } else {
                setCreditHistoryData([]);
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setloading(false);
        }
    }, [company_id]);

    // ✅ Single useEffect for pagination changes
    useEffect(() => {
        fetchCustomers(query, selectedPaymentType, currentOffset, currentLimit);
    }, [currentLimit, currentOffset]);

    // ✅ Stable debounced search using fetchCustomers
    const debouncedSearch = useCallback(
        debounce((value, type, limit) => {
            setloading(true);
            fetchCustomers(value, type, 0, limit);
        }, 1000),
        [fetchCustomers]
    );

    // ✅ Stable search handler passed to table
    const handleSearchAction = useCallback((searchText) => {
        setquery(searchText);
        if (!searchText) {
            fetchCustomers("", selectedPaymentType, 0, currentLimit);
        } else {
            debouncedSearch(searchText, selectedPaymentType, currentLimit);
        }
    }, [selectedPaymentType, currentLimit, debouncedSearch, fetchCustomers]);

    // ✅ Stable pagination handler passed to table
    const handlePagination = useCallback((values) => {
        setCurrentLimit(values.limit);
        setCurrentOffset(values.offset);
    }, []);

    const handlePaymentTypeChange = (event) => {
        const paymentType = event.target.value;
        setloading(true);
        setquery("");
        setSelectedPaymentType(paymentType);
        fetchCustomers("", paymentType, 0, currentLimit);
    };

    const getPaymentHistory = (pos_credit_id) => {
        getCreditPaymentHistory({ pos_credit_id })
            .then((res) => {
                if (res?.result[0]?.response.length > 0) {
                    setPaymentHistoryData(res?.result[0]?.response);
                } else {
                    setPaymentHistoryData([]);
                }
            })
            .catch((error) => console.error("Error fetching payment history:", error));
    };

    const showDrawer = (historyData) => {
        setDrawerData(historyData);
        setVisible(true);
        getPaymentHistory(historyData.pos_credit_id);
    };

    const onClose = () => setVisible(false);

    const CreateCustomer = async (data) => {
        setloading(true);
        const payload = {
            store_id: company_id, user_id,
            phone: data?.mobile, name: data?.userName, email: "",
            address_type_id: data?.address_type_id,
            add_field_1: data?.add_field_1, add_field_2: data?.add_field_2,
            add_field_3: data?.add_field_3, add_field_4: data?.add_field_4,
            credit_amount: data?.credit_amount, credit_as_on_date: data?.credit_as_on_date,
        };
        try {
            const res = await createNewCustomer(payload);
            if (res?.result?.status_code === 200) {
                toast.success(t(res?.result?.status));
                setOpenAddCustomer(false);
                fetchCustomers(query, selectedPaymentType, currentOffset, currentLimit);
            } else {
                toast.error(t(res?.result?.status));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setloading(false);
        }
    };

    const UpdateCustomer = async (data) => {
        setloading(true);
        const payload = {
            store_id: company_id, user_id, customer_id: data?.customer_id,
            phone: data?.mobile, name: data?.userName, email: "",
            address_type_id: data?.address_type_id,
            add_field_1: data?.add_field_1, add_field_2: data?.add_field_2,
            add_field_3: data?.add_field_3, add_field_4: data?.add_field_4,
            credit_amount: data?.credit_amount, credit_as_on_date: data?.credit_as_on_date,
        };
        try {
            const res = await updateExistingCustomer(payload);
            if (res?.result?.status_code === 200) {
                toast.success(t(res?.result?.status));
                setOpenAddCustomer(false);
                fetchCustomers(query, selectedPaymentType, currentOffset, currentLimit);
            } else {
                toast.error(t(res?.result?.status));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setloading(false);
        }
    };

    const handleExport = async () => {
        const payload = {
            lang: i18next.language === "ar" ? "ar_001" : "en_US",
            start_date: "2023-01-01",
            end_date: getCurrentDate(),
            search: "",
            company_id,
        };
        const response = await axiosInstance.post(URLS.CREDIT_HISTORY_EXCEL, payload);
        if (response?.data?.result?.status_code === 200) {
            setExportModalOpen(false);
            downloadFile(response?.data?.result?.file);
        } else {
            toast.error(response?.data?.result.status);
        }
    };

    const downloadFile = (base64Data) => {
        try {
            if (!base64Data || typeof base64Data !== "string" || base64Data.trim() === "") {
                alert("No valid file data available for download");
                return;
            }
            let cleanBase64 = base64Data.trim()
                .replace(/^data:[\w/+-]+;base64,/, "")
                .replace(/^["']|["']$/g, "")
                .replace(/\s/g, "");
            const padLength = 4 - (cleanBase64.length % 4);
            if (padLength < 4) cleanBase64 += "=".repeat(padLength);
            const bytes = new Uint8Array(atob(cleanBase64).split("").map(c => c.charCodeAt(0)));
            const header = new Uint8Array(bytes.buffer, 0, 8);
            let mimeType = "application/octet-stream", fileExtension = "bin";
            if (header[0] === 0x50 && header[1] === 0x4b) { mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"; fileExtension = "xlsx"; }
            else if (header[0] === 0xd0 && header[1] === 0xcf) { mimeType = "application/vnd.ms-excel"; fileExtension = "xls"; }
            const blob = new Blob([bytes], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `credit_history_${new Date().getTime()}.${fileExtension}`;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 100);
        } catch (error) {
            console.error("File download failed:", error);
        }
    };

    const handlePrint = () => {
        const content = printRef.current.innerHTML;
        const printWindow = window.open("", "", "width=800,height=600");
        printWindow.document.write(`<html><head><title>Customer Credit</title><style>body{font-family:Arial;font-size:12px;color:#000;padding:10mm}table{width:100%;border-collapse:collapse;margin-top:6px}th,td{border:1px solid #ccc;padding:4px;font-size:11px}th{background:#f0f0f0}</style></head><body>${content}</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    const thStyle = { border: "1px solid #ccc", padding: "8px", background: "#f5f5f5", textAlign: "left" };
    const tdStyle = { border: "1px solid #ccc", padding: "8px" };

    return (
        <>
            <div className="flex w-full flex-col overflow-y-auto bg-[#F6F7F9] pb-2 md:h-full">
                <Navbar heading="Customers" />
                <div className="custom-scrollbar ms-1 flex-grow overflow-y-auto overflow-hidden sm:ms-3">
                    <div className="me-1 mt-4 flex flex-col gap-3 sm:me-3 md:me-8 md:flex-row md:items-start md:justify-between">
                        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
                            <SearchBox
                                value={query}
                                searchResult={(values) => {
                                    setquery(values);
                                    debouncedSearch(values, selectedPaymentType, currentLimit);
                                }}
                                placeholder={t("Search Name/Phone Number")}
                            />
                            <div className="mb-2 w-full md:ms-3 md:w-[50%]">
                                <SelectBox
                                    placeholder={"all_orders"}
                                    name="filter"
                                    onChange={handlePaymentTypeChange}
                                    options={options}
                                    value={selectedPaymentType}
                                />
                            </div>
                        </div>
                        <div className="flex w-full gap-2 md:w-auto">
                            <button
                                className="mt-2 flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primaryBlue text-sm font-semibold text-white sm:w-44 md:mt-2"
                                onClick={() => setIsOpenImportCustomer(true)}
                            >
                                <UploadOutlined />
                                {t("Import")}
                            </button>
                            <button
                                className="mt-1 flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primaryBlue text-sm font-semibold text-white sm:w-44 md:mt-2"
                                onClick={() => {
                                    if (allStores || checkPermission("/pos/credithistory", isGinne)) {
                                        setOpenAddCustomer(true);
                                    }
                                }}
                            >
                                {t("Add Customer")}
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <ShimmerLoader />
                    ) : (
                        <div className="mt-3">
                            <NewCustomersTable
                                additionalData={creditHistoryData}
                                pagination={handlePagination}
                                onSearch={handleSearchAction}
                                showDrawer={showDrawer}
                                onEditLine={(data) => {
                                    setEditData(data);
                                    setOpenAddCustomer(true);
                                }}
                            />
                        </div>
                    )}
                </div>

                {exportModalOpen && (
                    <ExportCasInOutReportModal
                        heading={t("Download Credit History Report")}
                        isOpen={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={(dates) => handleExport(dates)}
                    />
                )}

                {openAddCustomer && (
                    <AddCustomerModal
                        onClose={() => { setEditData(null); setOpenAddCustomer(false); }}
                        confirm={(data) => CreateCustomer(data)}
                        showSkipButton={true}
                        editData={editData ?? null}
                        onUpdate={(data) => UpdateCustomer(data)}
                        loading={loading}
                    />
                )}

                {isOpenImportCustomer && (
                    <ImportCustomers
                        onClose={() => setIsOpenImportCustomer(false)}
                        isOpen={isOpenImportCustomer}
                    />
                )}
            </div>

            <Drawer
                title={t("Payment History")}
                placement="right"
                width={typeof window !== "undefined" && window.innerWidth < 768 ? "95%" : 700}
                onClose={onClose}
                visible={visible}
            >
                <div>
                    <div className="flex justify-end mb-4">
                        <button onClick={handlePrint} className="px-6 py-2 bg-[#1b4597] text-white rounded-lg -mt-2 font-semibold">
                            {t("Print")}
                        </button>
                    </div>
                    {paymentHistoryData.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-[620px] w-full">
                                <thead>
                                    <tr className="bg-gray-200">
                                        <th className="p-2 text-start">{t("Payment Date & Time")}</th>
                                        <th className="p-2 text-start">{t("amount")}</th>
                                        <th className="p-2 text-start">{t("Remark")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentHistoryData.map((payment, index) => (
                                        <tr key={index} className="border-b">
                                            <td className="p-2">{moment(payment.payment_date).format("YYYY-MM-DD HH:mm:ss")}</td>
                                            <td className="p-2">EGP {payment.paid_amount}</td>
                                            <td className="p-2">{t(payment.remark)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p>{t("No payment history available")}</p>
                    )}
                </div>
            </Drawer>

            <div style={{ display: "none" }}>
                <div ref={printRef}>
                    <h2>{t("Customer Details")}</h2>
                    <p><strong>{t("Name")}:</strong> {drawerData?.customer_name}</p>
                    <p><strong>{t("Phone")}:</strong> {drawerData?.mobile_no}</p>
                    <p><strong>{t("Address")}:</strong> {drawerData?.address}</p>
                    <h3>{t("Payment History")}</h3>
                    <table>
                        <thead>
                            <tr>
                                <th style={thStyle}>{t("Date")}</th>
                                <th style={thStyle}>{t("Paid")}</th>
                                <th style={thStyle}>{t("Remark")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentHistoryData.map((payment, index) => (
                                <tr key={index}>
                                    <td style={tdStyle}>{moment(payment.payment_date).format("YYYY-MM-DD HH:mm")}</td>
                                    <td style={tdStyle}>EGP {payment.paid_amount}</td>
                                    <td style={tdStyle}>{payment.remark || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default NewAllCustomers;